// REQ-P1-06b: 管理者アカウント管理画面の契約型付きAPI。

import { api, type paths } from './client';
import { ApiStatusError } from './templates';

type AdminUserEmailRequest = NonNullable<
  paths['/admin/admin-users/{id}/email']['put']['requestBody']
>['content']['application/json'];
type AdminUserEmailResponse = NonNullable<
  paths['/admin/admin-users/{id}/email']['put']['responses'][200]['content']
>['application/json'];
type AdminUserRoleRequest = NonNullable<
  paths['/admin/admin-users/{id}/role']['put']['requestBody']
>['content']['application/json'];
type AdminUserPermissionsRequest = NonNullable<
  paths['/admin/admin-users/{id}/permissions']['put']['requestBody']
>['content']['application/json'];
type CreateAdminInviteRequest = NonNullable<
  paths['/admin/invites']['post']['requestBody']
>['content']['application/json'];
type CreateAdminInviteResponse = NonNullable<
  paths['/admin/invites']['post']['responses'][200]['content']
>['application/json'];
type AdminEmailAvailableQuery = NonNullable<
  paths['/admin/email-available']['get']['parameters']['query']
>;
type AdminEmailAvailableResponse = NonNullable<
  paths['/admin/email-available']['get']['responses'][200]['content']
>['application/json'];
type ManagedNotificationSettingsRequest = NonNullable<
  paths['/admin/admin-users/{id}/notification-settings']['put']['requestBody']
>['content']['application/json'];
type ManagedNotificationSettingsResponse = NonNullable<
  paths['/admin/admin-users/{id}/notification-settings']['get']['responses'][200]['content']
>['application/json'];
type RevokeAdminUserTokenSessionsResponse = NonNullable<
  paths['/admin/admin-users/{id}/token-sessions/revoke']['post']['responses'][200]['content']
>['application/json'];

type ApiTextResult = {
  data?: string | unknown;
  error?: string | unknown;
  response: Response;
};

function getApiErrorMessage(error: unknown): string | undefined {
  if (!error) return undefined;
  if (typeof error === 'string') {
    try {
      const parsed = JSON.parse(error);
      if (parsed && typeof parsed === 'object') {
        const message = (parsed as Record<string, unknown>).message || (parsed as Record<string, unknown>).error;
        if (typeof message === 'string') return message;
      }
    } catch {
      return error;
    }
  }
  if (typeof error !== 'object') return undefined;
  const record = error as Record<string, unknown>;
  const message = record.message || record.error;
  return typeof message === 'string' ? message : undefined;
}

function throwApiError(status: number, error: unknown, fallbackMessage: string): never {
  const statusError = new ApiStatusError(status);
  statusError.message = getApiErrorMessage(error) || fallbackMessage;
  throw statusError;
}

function parseTextJson(value: unknown, fallbackMessage: string): Record<string, unknown> {
  if (value && typeof value === 'object') return value as Record<string, unknown>;
  if (typeof value !== 'string' || !value.trim()) return {};
  try {
    const parsed = JSON.parse(value);
    return parsed && typeof parsed === 'object' ? (parsed as Record<string, unknown>) : {};
  } catch {
    throw new Error(fallbackMessage);
  }
}

function parseApiTextJson(result: ApiTextResult, fallbackMessage: string): Record<string, unknown> {
  if (!result.response.ok) {
    throwApiError(result.response.status, result.error, fallbackMessage);
  }
  return parseTextJson(result.data, fallbackMessage);
}

export async function listAdminUsers(): Promise<Record<string, unknown>[]> {
  const { data, error, response } = await api.GET('/admin/admin-users');
  if (!response.ok || !data) throwApiError(response.status, error, '管理者一覧の取得に失敗しました。');
  const users = (data as { adminUsers?: unknown }).adminUsers;
  return Array.isArray(users) ? (users as Record<string, unknown>[]) : [];
}

export async function listDisabledAdminUsers(): Promise<Record<string, unknown>[]> {
  const { data, error, response } = await api.GET('/admin/admin-users/disabled');
  if (!response.ok || !data) {
    throwApiError(response.status, error, '停止中アカウント一覧の取得に失敗しました。');
  }
  const users = (data as { adminUsers?: unknown }).adminUsers;
  return Array.isArray(users) ? (users as Record<string, unknown>[]) : [];
}

export async function listAdminInvites(): Promise<Record<string, unknown>[]> {
  const { data, error, response } = await api.GET('/admin/invites');
  if (!response.ok || !data) throwApiError(response.status, error, '招待一覧の取得に失敗しました。');
  const invites = (data as { invites?: unknown }).invites;
  if (!Array.isArray(invites)) throw new Error('招待一覧を確認できませんでした。');
  return invites as Record<string, unknown>[];
}

export async function createAdminInvite(
  body: CreateAdminInviteRequest
): Promise<CreateAdminInviteResponse> {
  const { data, error, response } = await api.POST('/admin/invites', { body });
  if (!response.ok || !data) throwApiError(response.status, error, '招待の作成に失敗しました');
  return data as CreateAdminInviteResponse;
}

export async function checkAdminEmailAvailable(
  query: AdminEmailAvailableQuery
): Promise<boolean | null> {
  const { data, response } = await api.GET('/admin/email-available', {
    params: { query },
  });
  if ([400, 422, 409].includes(response.status)) throwApiError(response.status, null, 'メールアドレスを確認できませんでした。');
  if (!response.ok || !data) return null;
  const available = (data as AdminEmailAvailableResponse).available;
  return typeof available === 'boolean' ? available : null;
}

export async function updateAdminUserEmail(
  id: number | string,
  body: AdminUserEmailRequest
): Promise<AdminUserEmailResponse> {
  const { data, error, response } = await api.PUT('/admin/admin-users/{id}/email', {
    params: { path: { id: Number(id) } },
    body,
  });
  if (!response.ok || !data) throwApiError(response.status, error, 'メールアドレスの更新に失敗しました。');
  return data as AdminUserEmailResponse;
}

export async function getManagedAdminNotificationSettings(
  id: number | string
): Promise<ManagedNotificationSettingsResponse> {
  const { data, error, response } = await api.GET('/admin/admin-users/{id}/notification-settings', {
    params: { path: { id: Number(id) } },
  });
  if (!response.ok || !data) throwApiError(response.status, error, '通知設定の取得に失敗しました。');
  return data as ManagedNotificationSettingsResponse;
}

export async function updateManagedAdminNotificationSettings(
  id: number | string,
  body: ManagedNotificationSettingsRequest
): Promise<Record<string, unknown>> {
  const { data, error, response } = await api.PUT('/admin/admin-users/{id}/notification-settings', {
    params: { path: { id: Number(id) } },
    body,
  });
  if (!response.ok || !data) throwApiError(response.status, error, '通知設定の更新に失敗しました。');
  return data as Record<string, unknown>;
}

export async function getAdminUserPermissions(id: number | string): Promise<Record<string, unknown>> {
  const { data, error, response } = await api.GET('/admin/admin-users/{id}/permissions', {
    params: { path: { id: Number(id) } },
  });
  if (!response.ok || !data) throwApiError(response.status, error, '権限情報の取得に失敗しました。');
  return data as Record<string, unknown>;
}

export async function updateAdminUserPermissions(
  id: number | string,
  body: AdminUserPermissionsRequest
): Promise<Record<string, unknown>> {
  const result = await api.PUT('/admin/admin-users/{id}/permissions', {
    params: { path: { id: Number(id) } },
    body,
    parseAs: 'text',
  });
  return parseApiTextJson(result, '権限の更新に失敗しました。');
}

export async function updateAdminUserRole(
  id: number | string,
  body: AdminUserRoleRequest
): Promise<Record<string, unknown>> {
  const result = await api.PUT('/admin/admin-users/{id}/role', {
    params: { path: { id: Number(id) } },
    body,
    parseAs: 'text',
  });
  return parseApiTextJson(result, '権限の更新に失敗しました。');
}

export async function disableAdminUser(id: number | string): Promise<void> {
  const { error, response } = await api.DELETE('/admin/admin-users/{id}', {
    params: { path: { id: Number(id) } },
  });
  if (!response.ok) throwApiError(response.status, error, '停止に失敗しました');
}

export async function restoreAdminUser(id: number | string): Promise<void> {
  const { error, response } = await api.POST('/admin/admin-users/{id}/restore', {
    params: { path: { id: Number(id) } },
  });
  if (!response.ok) throwApiError(response.status, error, '再開に失敗しました');
}

export async function revokeAdminUserTokenSessions(
  id: number | string
): Promise<RevokeAdminUserTokenSessionsResponse> {
  const { data, error, response } = await api.POST('/admin/admin-users/{id}/token-sessions/revoke', {
    params: { path: { id: Number(id) } },
  });
  if (!response.ok || !data) throwApiError(response.status, error, '強制ログアウトに失敗しました');
  return data as RevokeAdminUserTokenSessionsResponse;
}
